import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TenantService from './services/tenantService.ts';
import { useGlobalStore } from '../../../application/store/store.tsx';
import {
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    CircularProgress,
} from '@mui/material';
import { Tenant } from '../../../application/types/tenants.ts';
import Loader from '../../../common/components/Loader.tsx';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const UpdateTenant = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [tenant, setTenant] = useState<Tenant>();
    const [isLoading, setIsLoading] = useState(false);
    const [expiryDate, setExpiryDate] = useState('');
    const tenantService = new TenantService();
    const { setMessage } = useGlobalStore((state) => state);

    useEffect(() => {
        const fetchTenant = async () => {
            try {
                setIsLoading(true);
                tenantService.getTenant(id).then((response) => {
                    setTenant(response);
                    const date = response?.validUpto
                        ?.toString()
                        .substring(0, 10);
                    setExpiryDate(date);
                    setIsLoading(false);
                });
            } catch (error) {
                setMessage('Kunde inte hämta organisationen', 'error', 6000);
                console.error(error);
                setIsLoading(false);
            }
        };

        fetchTenant();
    }, []);

    const handleUpdateLicense = async (_e) => {
        try {
            setIsLoading(true);
            await tenantService.updateLicens(tenant.id, expiryDate);
            setMessage('Licensen har uppdaterats', 'success', 6000);
        } catch (error) {
            setMessage('Kunde inte uppdatera licensen', 'error', 6000);
        } finally {
            setIsLoading(false);
        }
    };

    const handleActivate = async () => {
        try {
            setIsLoading(true);
            await tenantService.activateTenant(id);
            setMessage('Organisationen har aktiverats', 'success', 6000);
            setTenant({ ...tenant, isActive: true });
        } catch (error) {
            setMessage('Kunde inte aktivera organisationen', 'error', 6000);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeactivate = async () => {
        try {
            setIsLoading(true);
            await tenantService.deactivateTenant(id);
            setMessage('Organisationen har inaktiverats', 'success', 6000);
            setTenant({ ...tenant, isActive: false });
        } catch (error) {
            setMessage('Kunde inte inaktivera organisationen', 'error', 6000);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="px-4 py-6 sm:px-6">
            {/* Back button */}
            <div className="mb-4">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('..')}
                    color="primary"
                >
                    Tillbaka
                </Button>
            </div>

            {/* Main content */}
            <Paper
                elevation={3}
                className="mx-auto p-4 sm:p-6 rounded-2xl w-full max-w-3xl"
            >
                <Typography
                    variant="h4"
                    component="h1"
                    align="center"
                    gutterBottom
                    className="text-2xl sm:text-4xl font-bold mb-4"
                >
                    Uppdatera licensperiod
                </Typography>

                <Typography fontWeight="bold" className="mb-4">
                    Organisation: {tenant?.name}
                </Typography>

                <TextField
                    label="Licens t.o.m."
                    type="date"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        mt: 3,
                    }}
                >
                    <Button
                        startIcon={
                            isLoading ? (
                                <CircularProgress size={24} color="info" />
                            ) : null
                        }
                        variant="contained"
                        color="primary"
                        onClick={handleUpdateLicense}
                        fullWidth={true}
                        sx={{ maxWidth: { sm: 'fit-content' } }}
                    >
                        Uppdatera Licens
                    </Button>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2,
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            width: '100%',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Typography fontWeight="bold">
                            Status: {tenant?.isActive ? 'Aktiv' : 'Inaktiv'}
                        </Typography>
                        {tenant?.isActive ? (
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={handleDeactivate}
                            >
                                Inaktivera
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleActivate}
                            >
                                Aktivera
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>
        </div>
    );
};

export default UpdateTenant;
